import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import { Form, Modal, Select, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import { getInstitutions } from "../../services/institutionService";
import TextArea from "../../components/Inputs/TextAreaInput";
import {
  createFacility,
  deleteFacility,
  getFacilities,
  updateFacility,
} from "../../services/facilityService";
import { facilitySchema } from "../../validators/facilityValidators";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const Facility = () => {
  const [facilities, setFacilities] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [facilityToEdit, setFacilityToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [facilityToDelete, setFacilityToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");

  const { user } = useStateContext();
  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadFacilities();
    loadInstitutions();
  }, []);

  useEffect(() => {
    let filtered = facilities;
    if (searchTerm.trim() !== "") {
      filtered = facilities.filter((item) =>
        `${item.name} ${item.institution.name}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    setFilteredFacilities(filtered);
  }, [searchTerm, facilities, selectedInstitution]);

  const loadInstitutions = async () => {
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      toast.error("Failed to load institutions");
    }
  };

  const loadFacilities = async () => {
    setLoading(true);
    try {
      const { data } = await getFacilities();
      setFacilities(data.data);
      setFilteredFacilities(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load facilities");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (facilityToEdit) {
      onUpdateFacility(payload, resetForm);
    } else {
      onCreateFacility(payload, resetForm);
    }
  };

  const onCreateFacility = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createFacility(payload);

      setFacilities((prev) => [...prev, data.data]);

      toast.success("Facilities added successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Facility was not added, try again!"
        );
      }
    }
  };

  const handleEdit = (category) => {
    setFacilityToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdateFacility = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalFacilities = [...facilities];

      const facilityIndex = originalFacilities.findIndex(
        (facility) => facility.id === facilityToEdit.id
      );

      if (facilityIndex === -1) throw new Error("Facilties not found.");

      const { data } = await updateFacility(facilityToEdit.id, payload);

      originalFacilities[facilityIndex] = data.data;

      setFacilities(originalFacilities);

      setFacilityToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Facility updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Facility not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setFacilityToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteFacility = async () => {
    const originalFacilities = [...facilities];

    const newFacilities = originalFacilities.filter(
      (facility) => facility.id !== facilityToDelete.id
    );

    setFacilities(newFacilities);

    try {
      await deleteFacility(facilityToDelete?.id);

      toast.success("Facility deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Facility not deleted, try again later"
        }`
      );

      setFacilities(originalFacilities);
    } finally {
      setIsDeleteModalOpen(false);

      setFacilityToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setFacilityToEdit(null);
    setFacilityToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    {
      title: "Facility",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "left", color: "#888" }}>{displayText}</div>
        ) : (
          <Tooltip title={text}>
            <div
              style={{
                maxWidth: 250,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {text}
            </div>
          </Tooltip>
        );
      },
    },
    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: ["institution", "name"],
            key: "institution",
            render: (text) => (
              <span className="text-gray-700 text-sm">{text || "N/A"}</span>
            ),
          },
        ]
      : []),
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Facility">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Facility">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Specialized Facilities"
        subtitle1="Specialized Facilities"
      />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Specialized Facilities"
            count={filteredFacilities.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Facility"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredFacilities.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              !isInstAdmin && (
                <div className="mb-2 py-2 flex gap-2">
                  <Select
                    multiple={false}
                    id="institutions"
                    defaultValue={selectedInstitution}
                    className="w-full h-[36px] min-w-[250px]"
                    onChange={(value) => {
                      setSelectedInstitution(value);
                    }}
                    showSearch={true}
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    <Select.Option value={"Not Selected"}>
                      Filter by Institution
                    </Select.Option>
                    {institutions &&
                      institutions.length > 0 &&
                      institutions.map((option) => (
                        <Select.Option key={option.id} value={option.id}>
                          {option.name}
                        </Select.Option>
                      ))}
                  </Select>
                </div>
              )
            }
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {facilityToEdit ? "Edit Facility" : "Add Facility"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                name: facilityToEdit ? facilityToEdit?.name : "",
                description: facilityToEdit ? facilityToEdit?.description : "",
                institution_id: facilityToEdit
                  ? facilityToEdit?.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
              }}
              enableReinitialize
              validationSchema={facilitySchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                submitForm,
              }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    {isInstAdmin ? (
                      <input
                        type="hidden"
                        name="institution_id"
                        value={
                          facilityToEdit
                            ? facilityToEdit?.institution?.id
                            : user?.institution?.id
                        }
                      />
                    ) : (
                      <CustomDropDownInput
                        name="institution_id"
                        title="Institution"
                        placeholder="Select an institution"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={institutions}
                        setFieldValue={setFieldValue}
                      />
                    )}

                    <TextInput
                      name="name"
                      title="Facility"
                      placeholder="e.g. Renewable Energy Testing Lab (RETL)"
                      requiredField
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <TextArea
                      name="description"
                      title="Description"
                      placeholder="e.g. A state-of-the-art facility used for testing solar photovoltaic panels and microgrid technologies."
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteFacility}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default Facility;
